import React, { Fragment, useEffect, useState, useRef } from "react";
import { Button, Divider, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { CallState, IFlyerOption, IPerson, ITravelerStepErrors } from "redmond";
import { LoyaltyInfoFormComponentProps, TravelerSelectDropdown } from "halifax";
import { FlightTravelerSelectDropdownFormConnectorProps } from "./container";
import { FrequentFlyerForm } from "../FrequentFlyerForm/component";
import { getFrequentFlyerProgramFromTrip } from "./utils";
import "./styles.scss";
import { InfantSeatPickerModal } from "../../InfantSeatPickerModal";
import { useFlightBookPassengerSelect } from "../../FlightBookPassengerSelection";
import { getIsLapInfant } from "../../FlightBookPassengerSelection/useFlightBookPassengerSelect";
import {
  AVAILABLE,
  CORP_EMBEDDED_TRAVELER_FORM_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../../../context/experiments";

export interface FlightTravelerSelectDropdownFormProps
  extends FlightTravelerSelectDropdownFormConnectorProps {
  disableEditTravelerInfo: boolean;
  selectedTravelers: IPerson[];
  updateSelectedTravelersList: (traveler: IPerson) => void;
  removeSelectedTraveler: (traveler: IPerson) => void;
  showErrors: ITravelerStepErrors;
  setShowErrors: (showErrors: ITravelerStepErrors) => void;
}

export const FlightTravelerSelectDropdownForm = ({
  disableEditTravelerInfo,
  selectedTravelers,
  updateSelectedTravelersList,
  updateUserPassenger,
  removeSelectedTraveler,
  showErrors,
  setShowErrors,
  userPassengerCallState,
  fetchCorpUserPassengers,
  tripDetails,
  setUserSelectedPassengerIds,
  setUserSelectedLapInfantIds,
  setUserSelectedTravelersList,
  validatePassengers,
  userRoles,
}: FlightTravelerSelectDropdownFormProps) => {
  const [dropdownCount, setDropdownCount] = useState(
    selectedTravelers.length || 1
  );

  const expState = useExperiments();
  const useEmbeddedTravelerForm =
    getExperimentVariant(
      expState.experiments,
      CORP_EMBEDDED_TRAVELER_FORM_EXPERIMENT
    ) === AVAILABLE;

  const {
    selectedTravelerIds: selectedAdultTravelerIds,
    selectedLapInfantIds,
    travelers,
    viewedTripSummaryProperties,
    currentInfantToSelectSeat,
    setCurrentInfantToSelectSeat,
    handleSelectedTravelerIds,
    tripFinalFlightDate,
  } = useFlightBookPassengerSelect();

  const selectedTravelerIds = [
    ...selectedAdultTravelerIds,
    ...selectedLapInfantIds,
  ];

  const prevCallStateRef = useRef<CallState>(userPassengerCallState);

  useEffect(() => {
    fetchCorpUserPassengers();
  }, []);

  useEffect(() => {
    if (disableEditTravelerInfo) {
      setDropdownCount(selectedTravelers.length);
    }
  }, [disableEditTravelerInfo]);

  useEffect(() => {
    if (selectedTravelerIds.length) {
      const selectedTravelerIdsSet = new Set(selectedTravelerIds);

      // note: travelers can be updated elsewhere on the page, such as the passport modal, and we want to keep the selected travelers in sync
      const newSelectedTravelers = travelers.filter((traveler) =>
        selectedTravelerIdsSet.has(traveler.id)
      );

      setUserSelectedTravelersList(newSelectedTravelers);
    }
  }, [travelers]);

  useEffect(() => {
    const completedUpdatePassenger =
      prevCallStateRef.current === CallState.InProcess &&
      userPassengerCallState === CallState.Success;

    if (
      completedUpdatePassenger &&
      selectedTravelerIds.length &&
      selectedTravelerIds.every((id) =>
        travelers.some((traveler) => traveler.id === id)
      )
    ) {
      validatePassengers(selectedAdultTravelerIds, selectedLapInfantIds, false);
    }

    prevCallStateRef.current = userPassengerCallState;
  }, [
    selectedTravelerIds.length,
    travelers.length,
    userPassengerCallState,
    selectedAdultTravelerIds,
    selectedLapInfantIds,
    travelers,
    validatePassengers,
  ]);

  const frequentFlyerPrograms = getFrequentFlyerProgramFromTrip(tripDetails);

  const updateSelectedTraveler = (traveler: IPerson) => {
    updateSelectedTravelersList(traveler);
    // note: we need to call this function used in the old FlightBookPassengerSelection component
    // to hook up the rest of the booking flow with the existing infrastructure
    handleSelectedTravelerIds(
      [...selectedTravelerIds, traveler.id],
      [...travelers, traveler]
    );
  };

  return (
    <div className="traveler-select-dropdown-container">
      <div className="form-container">
        <Typography variant="h3" className="title">
          Select or add travelers
        </Typography>
        <Typography variant="body2" component="p" color="textPrimary">
          Select or add travelers for this flight booking.
        </Typography>
        {[...Array(dropdownCount)].map((_, index) => (
          <Fragment key={index}>
            {index > 0 && <Divider style={{ margin: "20px 0" }} />}
            <TravelerSelectDropdown
              travelers={travelers}
              setProgress={() => {}}
              disableEditTravelerInfo={disableEditTravelerInfo}
              selectedTravelers={selectedTravelers}
              updateSelectedTraveler={updateSelectedTraveler}
              removeSelectedTraveler={(traveler) => {
                removeSelectedTraveler(traveler);
                const travelerIds = selectedTravelerIds.filter(
                  (id) => id !== traveler.id
                );
                handleSelectedTravelerIds(travelerIds, [
                  ...travelers,
                  traveler,
                ]);
                setDropdownCount((prevCount) =>
                  prevCount === 1 ? 1 : prevCount - 1
                );
              }}
              index={index}
              updateUserPassenger={(payload, isNewTraveler) => {
                updateUserPassenger(
                  payload,
                  false,
                  false,
                  false,
                  isNewTraveler
                );
                if (getIsLapInfant(payload.person, tripFinalFlightDate)) {
                  setCurrentInfantToSelectSeat(payload.person.id);
                }
              }}
              showErrors={showErrors}
              setShowErrors={setShowErrors}
              loyaltyProgram={null}
              loading={userPassengerCallState === CallState.InProcess}
              renderLoyaltyComponent={
                frequentFlyerPrograms !== null
                  ? getRenderLoyaltyComponent(frequentFlyerPrograms)
                  : undefined
              }
              productType="flight"
              useEmbeddedTravelerForm={useEmbeddedTravelerForm}
              userRoles={userRoles}
            />
          </Fragment>
        ))}

        <Button
          variant="outlined"
          color="secondary"
          className="add-traveler-button"
          startIcon={<Add />}
          disabled={
            dropdownCount > Object.keys(selectedTravelers).length ||
            disableEditTravelerInfo
          }
          onClick={() => setDropdownCount((prevCount) => prevCount + 1)}
        >
          Add additional traveler
        </Button>
      </div>
      <InfantSeatPickerModal
        currentInfantToSelectSeat={currentInfantToSelectSeat}
        setCurrentInfantToSelectSeat={setCurrentInfantToSelectSeat}
        userSelectedPassengerIds={selectedAdultTravelerIds}
        userSelectedLapInfantIds={selectedLapInfantIds}
        setUserSelectedPassengerIds={(ids: string[]) => {
          setUserSelectedPassengerIds({
            userSelectedPassengerIds: ids,
            multiTicketType: viewedTripSummaryProperties?.multi_ticket_type,
          });
        }}
        setUserSelectedLapInfantIds={(ids: string[]) => {
          setUserSelectedLapInfantIds({
            userSelectedLapInfantIds: ids,
          });
        }}
        onExit={() =>
          setUserSelectedTravelersList(
            selectedTravelers.filter(
              (traveler) => traveler.id !== currentInfantToSelectSeat
            )
          )
        }
      />
    </div>
  );
};

const getRenderLoyaltyComponent =
  (frequentFlyerPrograms: IFlyerOption[]) =>
  ({ onChange, disabled, traveler }: LoyaltyInfoFormComponentProps) =>
    (
      <FrequentFlyerForm
        onChange={(flyerMap) => onChange(undefined, flyerMap)}
        disabled={disabled}
        traveler={traveler}
        frequentFlyerPrograms={frequentFlyerPrograms}
      />
    );
